/**
 * ============================================================================
 * ESCROW DETAILS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Main escrow details view component
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEscrow } from '../../hooks/useEscrow';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import EscrowStatus from './EscrowStatus';
import EscrowTimeline from './EscrowTimeline';
import EscrowRelease from './EscrowRelease';
import EscrowRefund from './EscrowRefund';
import AutoReleaseCountdown from '../common/AutoReleaseCountdown';
import Button from '../common/Button';

/**
 * Escrow Details Component
 * @returns {JSX.Element}
 */
const EscrowDetails = () => {
  const { escrowId } = useParams();
  const navigate = useNavigate();
  const { getEscrow, loadHistory, escrow, history, loading } = useEscrow(escrowId);
  const { showError } = useToast();
  const { language } = useI18n();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await Promise.all([getEscrow(escrowId), loadHistory(escrowId)]);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement de l\'escrow' : 'Failed to load escrow')
        );
        console.error('Load escrow error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (escrowId) {
      loadData();
    }
  }, [escrowId, getEscrow, loadHistory, showError, language]);

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!escrow) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          {language === 'fr' ? 'Escrow introuvable' : 'Escrow not found'}
        </p>
      </Card>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
          🔒 {language === 'fr' ? 'Détails de l\'escrow' : 'Escrow Details'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Gérez et surveillez votre escrow'
            : 'Manage and monitor your escrow'}
        </p>
      </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Escrow Status */}
            <EscrowStatus escrow={escrow} />

            {/* Auto-Release Countdown */}
            {escrow.autoReleaseAt && escrow.status === 'funded' && (
              <AutoReleaseCountdown
                autoReleaseAt={escrow.autoReleaseAt}
                deliveredAt={escrow.deliveredAt}
                onExpire={() => {
                  getEscrow(escrowId);
                }}
              />
            )}

            {/* Timeline */}
            <EscrowTimeline escrow={escrow} history={history} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Release Action */}
            {escrow.status === 'funded' && (
              <EscrowRelease
                escrowId={escrowId}
                escrow={escrow}
                onSuccess={() => {
                  getEscrow(escrowId);
                  loadHistory(escrowId);
                }}
              />
            )}

            {/* Refund Action (Admin) */}
            {(escrow.status === 'funded' || escrow.status === 'frozen') && (
              <EscrowRefund
                escrowId={escrowId}
                escrow={escrow}
                onSuccess={() => {
                  getEscrow(escrowId);
                  loadHistory(escrowId);
                }}
              />
            )}

            {/* Transaction Link */}
            {escrow.transactionId && (
              <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
                <h3 className="text-lg font-black text-gray-900 dark:text-white mb-4">
                  {language === 'fr' ? 'Transaction associée' : 'Associated Transaction'}
                </h3>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => navigate(`/transactions/${escrow.transactionId}`)}
                >
                  {language === 'fr' ? 'Voir la transaction' : 'View Transaction'} →
                </Button>
              </Card>
            )}

            {/* Protection Info */}
            <Card
              variant="outlined"
              className="bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 border-2 border-blue-200 dark:border-blue-800 animate-fadeInUp"
              style={{ animationDelay: '0.4s' }}
            >
              <h4 className="font-black text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <span className="text-xl">🔒</span>
                {language === 'fr' ? 'Protection escrow' : 'Escrow Protection'}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {language === 'fr'
                  ? 'Vos fonds sont sécurisés en escrow jusqu\'à confirmation de la livraison. Vous pouvez approuver la libération ou soulever un problème si nécessaire.'
                  : 'Your funds are secured in escrow until delivery is confirmed. You can approve the release or raise an issue if needed.'}
              </p>
            </Card>
          </div>
        </div>
      </>
  );
};

export default EscrowDetails;
